// ============================================
// Survey Logic Types (Phase 1.1)
// ============================================

// Logic operators for condition evaluation
export type LogicOperator = 
  | 'equals' 
  | 'not_equals' 
  | 'contains' 
  | 'not_contains'
  | 'greater_than' 
  | 'less_than' 
  | 'greater_than_or_equals'
  | 'less_than_or_equals'
  | 'in' 
  | 'not_in'
  | 'is_empty' 
  | 'is_not_empty'
  | 'any'                                // Any answer triggers
  | 'must_select'                        // Required selection
  // Heat Map operators
  | 'clicked_region'                     // User clicked in specific region
  | 'click_count_equals'                 // Number of clicks equals value
  | 'click_count_gte'                    // Number of clicks >= value
  | 'click_count_lte'                    // Number of clicks <= value
  // Pick Group Rank operators
  | 'item_in_group'                      // Specific item was placed in group
  | 'group_contains'                     // Group contains specific item
  | 'group_count_equals'                 // Group has exactly N items
  | 'group_count_gte'                    // Group has >= N items
  | 'all_items_sorted'                   // All items have been sorted (none in pool)
  // Matrix/Grid operators (for radio_grid and checkbox_grid)
  | 'row_equals'                         // Specific row has specific column selected
  | 'row_not_equals';                    // Specific row does NOT have specific column selected

// Skip/routing action types
export type SkipAction = 
  | 'skip_to_question' 
  | 'skip_to_end' 
  | 'terminate'
  | 'continue';

// Termination types for screen-outs and completions
export type TerminationType = 
  | 'screen_out' 
  | 'completed' 
  | 'quota_full' 
  | 'thank_and_close'
  | 'disqualified';

// Question types supported by the survey engine
export type QuestionTypeEnum = 
  | 'multiple_choice' 
  | 'single_choice' 
  | 'open_text' 
  | 'likert_scale' 
  | 'dropdown' 
  | 'ranking' 
  | 'rating'
  | 'radio_grid' 
  | 'checkbox_grid' 
  | 'dropdown_matrix' 
  | 'text_matrix' 
  | 'numeric_matrix' 
  | 'ranking_matrix'
  | 'date_time' 
  | 'file_upload' 
  | 'slider_range' 
  | 'video_response'
  | 'image_choice'
  | 'heat_map'
  | 'pick_group_rank';

// ============================================
// Export Format Types
// ============================================

/**
 * Controls how option answers are formatted in CSV exports.
 * - "code_only": Export just the option code (e.g., "1")
 * - "text_only": Export just the text label (e.g., "To relax") - DEFAULT
 * - "code_and_text": Export both (e.g., "1. To relax")
 */
export type AnswerExportFormat = "code_only" | "text_only" | "code_and_text";

// Option with stable ID for reliable logic linking
export interface OptionItem {
  id: string;      // UUID, assigned on creation, never changes
  text: string;    // Display text (user-editable)
}

// Grid condition value for row_equals/row_not_equals operators
// Note: Only applies to radio_grid and checkbox_grid types, NOT dropdown_matrix,
// text_matrix, numeric_matrix, or ranking_matrix (which have different answer formats)
export interface GridConditionValue {
  rowId: string;      // ID from question.rows[]
  columnId: string;   // ID from question.columns[]
}

// Logic condition for evaluating responses
export interface LogicCondition {
  questionId: string;                    // Reference to question ID
  originalQuestionNumber?: string;       // Original Q number (e.g., "Q4", "S13")
  operator: LogicOperator;
  value: string | string[] | number | boolean | GridConditionValue;
  optionCode?: string;                   // For code-based conditions (e.g., "CODE J")
  brandOrRowId?: string;                 // For grid/matrix brand-specific logic
  // Heat Map condition fields
  regionId?: string;                     // For clicked_region operator
  // Pick Group Rank condition fields
  groupId?: string;                      // For item_in_group, group_contains, group_count_* operators
  itemId?: string;                       // For item_in_group operator (which item to check)
}

// Skip logic rule
export interface SkipLogicRule {
  conditions: LogicCondition[];
  conditionOperator: 'AND' | 'OR';
  action: SkipAction;
  targetQuestionId?: string;             // For skip_to_question
  targetOriginalNumber?: string;         // For reference (e.g., "Q4", "S13")
  terminationType?: TerminationType;     // For terminate action
  terminationMessage?: string;           // Custom termination message
}

// Display condition for showing/hiding questions
export interface DisplayCondition {
  conditions: LogicCondition[];
  conditionOperator: 'AND' | 'OR';
  show: boolean;                         // true = show if met, false = hide if met
}

// Option-level logic
export interface OptionLogic {
  optionId: string;
  optionValue: string;
  optionCode?: string;                   // Code like "1", "A", "J"
  skipLogic?: SkipLogicRule;
  isAnchor?: boolean;                    // [ANCHOR]
  isExclusive?: boolean;                 // [EXCLUSIVE]
  isMustSelect?: boolean;                // [MUST SELECT]
  mustSelectForBrand?: string;           // MUST SELECT FOR [BRAND]
}

// Segmentation rule for quotas
export interface SegmentationRule {
  segmentName: string;                   // e.g., "LIGHT", "FAN", "GROUP 1"
  conditions: LogicCondition[];
  conditionOperator: 'AND' | 'OR';       // How to combine conditions
  quotaMin?: number;                     // Target minimum responses
  quotaMax?: number;                     // Close segment when reached
  groupId?: string;                      // For per-group quotas (e.g., wave, panel)
}

// For grid questions with row-specific logic
export interface GridRow {
  id: string;
  label: string;                         // e.g., "Fanta", "Coca-Cola"
  code?: string;
}

export interface GridColumn {
  id: string;
  label: string;                         // e.g., "Never", "Once a week"
  code?: string;
}

export interface RowLogic {
  rowId: string;
  columnConditions: {
    columnId: string;
    action: SkipAction;
    terminationType?: TerminationType;
  }[];
}

// ============================================
// Image Choice Question Type
// ============================================

// Image option for image_choice question type
export interface ImageOption {
  url: string;              // R2 key or external URL
  caption?: string;         // Optional caption/label below image
  altText?: string;         // Accessibility description for screen readers
}

// Image choice configuration
export interface ImageChoiceOptions {
  images: ImageOption[];
  selectionMode: "single" | "multiple";  // Radio buttons vs checkboxes
  allowNoneOfAbove?: boolean;            // "None of the above" option
  noneOfAboveLabel?: string;             // Custom label (default: "None of the above")
}

// Date/Time configuration for date_time question type
export interface DateTimeConfig {
  mode: "datetime" | "date" | "time";
  minuteStep?: number;  // Time picker granularity in minutes (default: 1)
  min?: string;         // Future: minimum allowed value
  max?: string;         // Future: maximum allowed value
}

// Default config for date_time questions - frozen to prevent accidental mutation
export const DEFAULT_DATE_TIME_CONFIG = Object.freeze({
  mode: "date",
  minuteStep: 1,
} satisfies DateTimeConfig);

// ============================================
// Heat Map Question Type
// ============================================

// Heat map region definitions (hidden from respondents by default, used for reporting)
export type HeatMapRegion =
  | {
      id: string;
      name: string;           // For reporting ("Logo", "CTA Button")
      shape: "rect";
      xPct: number;           // 0-100
      yPct: number;
      wPct: number;
      hPct: number;
    }
  | {
      id: string;
      name: string;
      shape: "poly";
      pointsPct: Array<{ xPct: number; yPct: number }>;  // Minimum 3 points
    };

// Heat map question configuration
export interface HeatMapConfig {
  imageKey: string;                       // R2 key for the image
  maxClicks: number;                      // Default: 1
  replaceOldest: boolean;                 // Default: true (industry standard)
  allowDeselect: boolean;                 // Default: true (click dot to remove)
  regions?: HeatMapRegion[];              // Hidden reporting regions
  regionsVisibleToRespondent?: boolean;   // Default: false
  // Captured at upload for pixel coordinate calculation (source of truth for image)
  imageNaturalWidth?: number;
  imageNaturalHeight?: number;
}

// Default config for heat_map questions - frozen to prevent accidental mutation
export const DEFAULT_HEAT_MAP_CONFIG = Object.freeze({
  imageKey: "",
  maxClicks: 1,
  replaceOldest: true,
  allowDeselect: true,
  regionsVisibleToRespondent: false,
} satisfies HeatMapConfig);

// Heat map click data - ALWAYS compact slots 1..n (no gaps)
export interface HeatMapClick {
  slot: number;                // 1..n, always compact (no gaps)
  xPct: number;                // 0-100, clamped
  yPct: number;                // 0-100, clamped
  xPx: number;                 // Pixel X relative to natural dims (0-indexed: 0..width-1)
  yPx: number;                 // Pixel Y relative to natural dims (0-indexed: 0..height-1)
  regionId?: string | "other"; // undefined if no regions, "other" if outside all regions
  ts: number;                  // Timestamp for replace-oldest logic
}

// Heat map answer - clicks are ALWAYS compact: [slot:1, slot:2, ...] no gaps
export interface HeatMapAnswer {
  clicks: HeatMapClick[];
  imageNaturalWidth: number;   // Dims at response time for audit trail
  imageNaturalHeight: number;
}

// Type guard for heat map answers
export function isHeatMapAnswer(answer: unknown): answer is HeatMapAnswer {
  return (
    answer !== null &&
    typeof answer === "object" &&
    "clicks" in answer &&
    Array.isArray((answer as HeatMapAnswer).clicks) &&
    "imageNaturalWidth" in answer &&
    "imageNaturalHeight" in answer
  );
}

// ============================================
// Pick, Group, & Rank Question Type
// ============================================

// Item definition - ALWAYS use id+label, never raw strings
// This supports duplicate labels ("Other", "None", etc.) safely
export interface PickGroupRankItem {
  id: string;           // Unique identifier
  label: string;        // Display text
}

// Group definition for pick_group_rank questions
export interface PickGroupRankGroup {
  id: string;           // Unique identifier
  label: string;        // Display name
}

// Pick, Group, & Rank configuration
export interface PickGroupRankConfig {
  items: PickGroupRankItem[];               // The items to sort (pool) - id+label
  groups: PickGroupRankGroup[];             // Target groups/buckets
  allowRankingWithinGroups: boolean;        // Enable ranking within each group
  requireAllItemsSorted: boolean;           // All items must leave the pool
  validation?: {
    minPerGroup?: number;                   // Minimum items per group
    maxPerGroup?: number;                   // Maximum items per group
    minTotalSorted?: number;                // Minimum total items that must be sorted
  };
  displayMode: "columns" | "stacked";       // Layout style
  stackItemsInGroups: boolean;              // Compact display within groups
  showItemPool: boolean;                    // Show unsorted pool
  maxRankColumnsExport?: number;            // Cap rank columns in export (default: 10)
}

// Default configuration - frozen to prevent accidental mutation
export const DEFAULT_PICK_GROUP_RANK_CONFIG = Object.freeze({
  items: [
    { id: "item-1", label: "Item 1" },
    { id: "item-2", label: "Item 2" },
    { id: "item-3", label: "Item 3" },
    { id: "item-4", label: "Item 4" },
    { id: "item-5", label: "Item 5" },
  ],
  groups: [
    { id: "group-1", label: "Group 1" },
    { id: "group-2", label: "Group 2" },
  ],
  allowRankingWithinGroups: false,
  requireAllItemsSorted: false,
  displayMode: "columns" as const,
  stackItemsInGroups: false,
  showItemPool: true,
  maxRankColumnsExport: 10,
} satisfies PickGroupRankConfig);

// Pick, Group, & Rank answer structure - stores IDs, not labels
export interface PickGroupRankAnswer {
  groups: Record<string, string[]>;  // groupId -> ordered item IDs
  unsorted: string[];                // Item IDs in pool
}

// Type guard for pick_group_rank answers - STRICT validation
export function isPickGroupRankAnswer(answer: unknown): answer is PickGroupRankAnswer {
  if (answer === null || typeof answer !== "object") return false;
  
  const obj = answer as Record<string, unknown>;
  
  // Must have groups object
  if (!("groups" in obj) || typeof obj.groups !== "object" || obj.groups === null) {
    return false;
  }
  
  // Must have unsorted array
  if (!("unsorted" in obj) || !Array.isArray(obj.unsorted)) {
    return false;
  }
  
  // Validate groups values are arrays of strings
  for (const value of Object.values(obj.groups as Record<string, unknown>)) {
    if (!Array.isArray(value)) return false;
    for (const item of value) {
      if (typeof item !== "string") return false;
    }
  }
  
  // Validate unsorted items are strings
  for (const item of obj.unsorted) {
    if (typeof item !== "string") return false;
  }
  
  return true;
}

// Helper to get item label by ID
export function getItemLabel(
  config: PickGroupRankConfig, 
  itemId: string
): string {
  return config.items.find(i => i.id === itemId)?.label ?? itemId;
}

export interface QuestionType {
  id: string;
  type: QuestionTypeEnum;
  question: string;
  options?: OptionItem[];
  scale?: {
    min: number;
    max: number;
    labels?: string[];
  };
  // Dropdown options for dropdown_matrix questions (top-level, standardized format)
  dropdownMatrixOptions?: {
    shared?: string[]; // Shared options for all cells
    perColumn?: { [columnIndex: number]: string[] }; // Custom options per column
  };
  validation?: {
    min?: number;
    max?: number;
    required?: boolean;
    fileTypes?: string[];
    maxFileSize?: number;
  };
  sliderRange?: {
    min: number;
    max: number;
    step?: number;
    labels?: string[];
  };
  videoConfig?: {
    maxDuration: number;      // Max recording time in seconds (e.g., 60, 120, 300)
    allowRetake: boolean;     // Whether user can re-record before submitting
  };
  imageOptions?: ImageChoiceOptions;  // Configuration for image_choice questions
  dateTimeConfig?: DateTimeConfig;    // Configuration for date_time questions (enforced at creation)
  heatMapConfig?: HeatMapConfig;      // Configuration for heat_map questions
  pickGroupRankConfig?: PickGroupRankConfig;  // Configuration for pick_group_rank questions
  required: boolean;
  allowOther?: boolean;       // Allow "Other (please specify)" option for choice questions
  otherLabel?: string;        // Custom label for the "Other" option (default: "Other (please specify)")
  
  // =============================================
  // Survey Logic Fields (Phase 1.1)
  // =============================================
  
  description?: string;                  // Question description/instruction text
  
  // originalQuestionNumber: Human-readable question identifier
  // - AI imports: REQUIRED, extracted from source document (Q1, Q4a, S13)
  // - Manual creation: Auto-generated (Q1, Q2, Q3a for insertions)
  originalQuestionNumber?: string;
  section?: string;                      // Section identifier
  isScreeningQuestion?: boolean;         // Mark as screening question
  
  // Option-level configurations
  optionLogic?: OptionLogic[];           // Per-option routing/logic
  randomizeOptions?: boolean;            // [RANDOMISE] option order
  
  // Question-level logic
  skipLogic?: SkipLogicRule[];           // Question-level skip rules
  displayConditions?: DisplayCondition[]; // Show/hide conditions
  segmentationRules?: SegmentationRule[]; // Quota/segmentation rules
  
  // Grid/Matrix specific logic
  rows?: GridRow[];                      // Grid row definitions
  columns?: GridColumn[];                // Grid column definitions
  rowLogic?: RowLogic[];                 // Brand-specific logic for grids
  
  // Instructions
  recordInstruction?: string;            // "[RECORD]", "[RECORD EXACT AGE]"
}

// Survey settings interface (extracted for reuse)
export interface SurveySettings {
  anonymous?: boolean;
  allowMultipleResponses?: boolean;
  // Question Randomization (Phase 1.1)
  randomizeQuestions?: boolean;          // Randomize question order
  anchoredQuestionIds?: string[];        // Questions that stay in place (intro, consent, demographics)
  // Quota Management (Quota Frontend Guide Phase 1.1)
  quotasEnabled?: boolean;               // Enable quota tracking and enforcement
}

export interface Survey {
  id: number;
  userId: string;
  title: string;
  description?: string;
  questions: QuestionType[];
  settings: SurveySettings;
  status: "draft" | "published" | "archived";
  originalFileName?: string;
  originalFileType?: string;
  // Survey distribution fields
  shareToken?: string;
  publishedAt?: string;
  closesAt?: string;
  createdAt: string;
  updatedAt: string;
  // Question Randomization (Phase 1.1)
  randomizeQuestions?: boolean;          // Convenience field (also in settings)
  anchoredQuestionIds?: string[];        // Convenience field (also in settings)
  // Response count (included when fetching surveys list from /api/surveys)
  responseCount?: number;
}

// Quota Status (Quota Frontend Guide Phase 2.1)
// Matches backend/src/services/quotaService.ts
export interface QuotaStatus {
  segmentName: string;
  groupId: string | null;       // null for global quotas, string for per-group
  quotaMin: number;             // Always present (default 0)
  quotaMax: number | null;      // null = unlimited
  currentCount: number;
  isOpen: boolean;              // true = accepting responses, false = quota full
  remainingSlots: number | null; // null if quotaMax is null (unlimited)
}

// File upload answer structure
export interface FileUploadAnswer {
  key: string;
  filename: string;
  size: number;
  mimetype: string;
  duration?: number; // For video files
}

// Survey answer can be various types
export type SurveyAnswerValue = 
  | string 
  | string[] 
  | number 
  | boolean 
  | FileUploadAnswer 
  | HeatMapAnswer              // For heat_map questions
  | PickGroupRankAnswer        // For pick_group_rank questions
  | Record<string, string | number> // For matrix answers
  | null 
  | undefined;

export interface SurveyResponse {
  id: number;
  surveyId: number;
  respondentId?: string;
  answers: Record<string, SurveyAnswerValue>; // { questionId: answer }
  metadata?: {
    userAgent?: string;
    language?: string;
    submittedAt?: string;
  };
  startedAt?: string;
  completedAt?: string;
  isComplete: boolean;
  createdAt: string;
}

// Processing job result structure
export interface ProcessingJobResult {
  title?: string;
  description?: string;
  questions?: QuestionType[];
}

export interface ProcessingJob {
  id: number;
  userId: string;
  fileName: string;
  fileType: string;
  status: "pending" | "processing" | "completed" | "failed";
  progress: number;
  result?: ProcessingJobResult;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

// Type guard for file upload answers
export function isFileUploadAnswer(answer: unknown): answer is FileUploadAnswer {
  return (
    answer !== null &&
    typeof answer === "object" &&
    "key" in answer &&
    "filename" in answer
  );
}

// ============================================
// Survey Validation (Phase 1.4)
// ============================================

export interface SurveyValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validate survey structure, with different rules for AI imports vs manual creation.
 * 
 * @param survey - The survey to validate
 * @param isAIImport - If true, applies stricter validation (originalQuestionNumber required)
 * @returns Validation result with errors and warnings
 */
export function validateSurvey(
  survey: Survey, 
  isAIImport: boolean
): SurveyValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  survey.questions.forEach((q, i) => {
    // Validate originalQuestionNumber
    if (!q.originalQuestionNumber) {
      if (isAIImport) {
        // HARD ERROR for AI imports - originalQuestionNumber is required
        errors.push(`Question ${i + 1}: Missing originalQuestionNumber (required for AI imports)`);
      } else {
        // WARNING for manual surveys - auto-generation should prevent this
        warnings.push(`Question ${i + 1}: Missing originalQuestionNumber, using Q${i + 1} as fallback`);
      }
    }
    
    // Validate required question text
    if (!q.question || q.question.trim() === '') {
      warnings.push(`Question ${i + 1}: Empty question text`);
    }
    
    // Validate options for choice-based questions
    if (['single_choice', 'multiple_choice', 'dropdown'].includes(q.type)) {
      if (!q.options || q.options.length < 2) {
        errors.push(`Question ${i + 1} (${q.originalQuestionNumber || `Q${i + 1}`}): Choice questions require at least 2 options`);
      }
    }
  });
  
  return { 
    valid: errors.length === 0, 
    errors, 
    warnings 
  };
}

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string;
}

// ============================================
// "Other" Option Support Helpers
// ============================================

/**
 * Special ID used for the virtual "Other (please specify)" option.
 * When users select "Other", answers are stored as "__other__:<free text>".
 */
export const OTHER_OPTION_ID = "__other__";

/**
 * Gets all selectable options for a question, including the synthetic
 * "Other (please specify)" option if allowOther is enabled.
 * 
 * Use this in logic builders to populate option dropdowns.
 */
export function getSelectableOptions(
  question: QuestionType
): Array<{ id: string; text: string }> {
  const options: Array<{ id: string; text: string }> = [
    ...(question.options || []),
  ];
  
  if (question.allowOther) {
    options.push({
      id: OTHER_OPTION_ID,
      text: question.otherLabel || "Other (please specify)",
    });
  }
  
  return options;
}

/**
 * Checks if an answer value represents an "Other" selection.
 * 
 * "Other" answers are stored as "__other__:<user's text>".
 * This handles both single-select (string) and multi-select (string[]).
 */
export function isOtherAnswer(answer: unknown): boolean {
  if (typeof answer === "string") {
    return answer === OTHER_OPTION_ID || answer.startsWith(`${OTHER_OPTION_ID}:`);
  }
  if (Array.isArray(answer)) {
    return answer.some(
      (a) => typeof a === "string" && 
             (a === OTHER_OPTION_ID || a.startsWith(`${OTHER_OPTION_ID}:`))
    );
  }
  return false;
}
